/**
 * Labels PT-BR + helpers das pessoas relacionadas de um Bond (ADR-129).
 *
 * Os 39 tipos de relacionamento vêm do enum canônico `BOND_RELATIONSHIP_TYPES`
 * (@lume/shared) — importados, não redefinidos. RELATIONSHIP_GROUPS apenas
 * organiza os mesmos códigos em seções para o seletor; uma verificação de
 * exaustividade garante que todo tipo do enum tenha rótulo e pertença a um grupo
 * (espelha o SCOPE_GROUPS do B1 Contatos Autorizados).
 *
 * `source` e `status` também vêm de enums canônicos; só os rótulos são locais.
 * No formulário de adição expomos apenas as duas fontes *declaradas por humano*
 * (`declared-by-operator` default + `declared-by-titular`) — `sync-from-external`
 * e `inferred` chegam por outros fluxos e não devem ser fabricados na UI.
 */

import {
  BOND_RELATED_PERSON_SOURCES,
  BOND_RELATIONSHIP_TYPES,
  type BondRelatedPersonSource,
  type BondRelationshipType,
} from '@lume/shared';

export type RelatedPersonStatus = 'active' | 'terminated';

export const RELATIONSHIP_LABELS: Record<BondRelationshipType, string> = {
  // Família
  spouse: 'Cônjuge',
  'ex-spouse': 'Ex-cônjuge',
  parent: 'Genitor(a)',
  mother: 'Mãe',
  father: 'Pai',
  child: 'Filho(a)',
  son: 'Filho',
  daughter: 'Filha',
  sibling: 'Irmão(ã)',
  brother: 'Irmão',
  sister: 'Irmã',
  grandparent: 'Avô/Avó',
  grandchild: 'Neto(a)',
  dependent: 'Dependente',
  'in-law': 'Parente por afinidade',
  // Tutela legal
  'legal-guardian': 'Responsável legal',
  'court-appointed-guardian': 'Curador nomeado judicialmente',
  'temporary-guardian': 'Responsável temporário',
  'emergency-contact': 'Contato de emergência',
  // Empresarial
  employee: 'Funcionário(a)',
  employer: 'Empregador(a)',
  'business-partner': 'Sócio(a)',
  shareholder: 'Acionista',
  'majority-owner': 'Sócio majoritário',
  // Cuidado
  caregiver: 'Cuidador(a)',
  'home-aide': 'Cuidador domiciliar',
  nurse: 'Enfermeiro(a)',
  // Beneficiário
  beneficiary: 'Beneficiário(a)',
  guarantor: 'Fiador(a)',
  // Externo
  lawyer: 'Advogado(a)',
  'external-counsel': 'Advogado externo',
  representative: 'Representante',
  // Educação
  classmate: 'Colega de turma',
  teacher: 'Professor(a)',
  // Outros
  witness: 'Testemunha',
  'co-defendant': 'Corréu',
  'related-to': 'Relacionado(a)',
};

export interface RelationshipGroup {
  label: string;
  types: readonly BondRelationshipType[];
}

export const RELATIONSHIP_GROUPS: readonly RelationshipGroup[] = [
  {
    label: 'Família',
    types: [
      'spouse',
      'ex-spouse',
      'parent',
      'mother',
      'father',
      'child',
      'son',
      'daughter',
      'sibling',
      'brother',
      'sister',
      'grandparent',
      'grandchild',
      'dependent',
      'in-law',
    ],
  },
  {
    label: 'Tutela legal',
    types: [
      'legal-guardian',
      'court-appointed-guardian',
      'temporary-guardian',
      'emergency-contact',
    ],
  },
  {
    label: 'Empresarial',
    types: ['employee', 'employer', 'business-partner', 'shareholder', 'majority-owner'],
  },
  { label: 'Cuidado', types: ['caregiver', 'home-aide', 'nurse'] },
  { label: 'Beneficiário', types: ['beneficiary', 'guarantor'] },
  { label: 'Externo', types: ['lawyer', 'external-counsel', 'representative'] },
  { label: 'Educação', types: ['classmate', 'teacher'] },
  { label: 'Outros', types: ['witness', 'co-defendant', 'related-to'] },
];

// Exaustividade: todo tipo do enum aparece em exatamente um grupo. Falha em
// build se o enum crescer e o grupo não for atualizado (evita tipo órfão no form).
const GROUPED = new Set(RELATIONSHIP_GROUPS.flatMap((g) => g.types));
const MISSING_FROM_GROUPS = BOND_RELATIONSHIP_TYPES.filter((t) => !GROUPED.has(t));
if (MISSING_FROM_GROUPS.length > 0) {
  throw new Error(
    `RELATIONSHIP_GROUPS desatualizado — tipos sem grupo: ${MISSING_FROM_GROUPS.join(', ')}`,
  );
}

export const SOURCE_LABELS: Record<BondRelatedPersonSource, string> = {
  'declared-by-titular': 'Declarado pelo titular',
  'declared-by-operator': 'Declarado pelo operador',
  'sync-from-external': 'Sincronizado de sistema externo',
  inferred: 'Inferido',
};

/**
 * Fontes ofertadas no formulário de adição (quem cadastra é um humano). As
 * demais (`sync-from-external`/`inferred`) vêm de outros fluxos — não fabricar.
 * Default = operador (a tela é operada pelo atendente).
 */
export const ADD_SOURCES: readonly BondRelatedPersonSource[] = [
  'declared-by-operator',
  'declared-by-titular',
];

// Exaustividade defensiva: garante que as fontes ofertadas existam no enum real.
const SOURCE_SET = new Set<string>(BOND_RELATED_PERSON_SOURCES);
const INVALID_ADD_SOURCES = ADD_SOURCES.filter((s) => !SOURCE_SET.has(s));
if (INVALID_ADD_SOURCES.length > 0) {
  throw new Error(`ADD_SOURCES contém fonte fora do enum: ${INVALID_ADD_SOURCES.join(', ')}`);
}

export const STATUS_LABELS: Record<RelatedPersonStatus, string> = {
  active: 'Ativa',
  terminated: 'Encerrada',
};

/** Rótulo de um tipo; fallback para o código cru (forward-compat se o tenant
 * declarar tipo novo antes do frontend ser atualizado). */
export function relationshipLabel(type: string): string {
  return RELATIONSHIP_LABELS[type as BondRelationshipType] ?? type;
}

export function sourceLabel(source: string | null): string {
  if (source === null) return '—';
  return SOURCE_LABELS[source as BondRelatedPersonSource] ?? source;
}

export function statusLabel(status: string): string {
  return STATUS_LABELS[status as RelatedPersonStatus] ?? status;
}

/** Data ISO → dd/mm/aaaa; null/inválida → '—'. */
export function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR', { dateStyle: 'medium' });
}
